/**
 * Panels you can drag wider or narrower.
 *
 * One hook for every splitter in the app, so they all behave the same: drag
 * the grip, the panel follows the pointer exactly, and the width is remembered
 * per browser. Arrow keys nudge it, Home and End take it to its limits, and a
 * double-click puts it back to the default.
 *
 * The drag writes the width straight onto the element each frame rather than
 * through React - re-rendering a conversation sixty times a second is what
 * made the old crew-chat drag lag - and the size is only committed on release.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

export type Side = 'left' | 'right';

type Options = {
  /** localStorage key; the width is remembered under it. */
  key: string;
  /** Width before anything is dragged. */
  initial: number;
  min: number;
  max: number;
  /** Which edge the grip sits on: 'left' grows the panel by dragging left. */
  side?: Side;
  /** Applied while dragging, so the panel can lose its transition. */
  onDragChange?: (dragging: boolean) => void;
};

function read(key: string, fallback: number, min: number, max: number): number {
  try {
    const raw = Number(localStorage.getItem(key));
    if (!Number.isFinite(raw) || raw <= 0) return fallback;
    return Math.max(min, Math.min(max, raw));
  } catch {
    return fallback;
  }
}

export function useSplitter({ key, initial, min, max, side = 'left', onDragChange }: Options) {
  const [width, setWidth] = useState(() => read(key, initial, min, max));
  const [dragging, setDragging] = useState(false);
  const panel = useRef<HTMLElement | null>(null);
  const live = useRef(width);

  /* The window shrinking must not leave a panel wider than the room there is. */
  useLayoutEffect(() => {
    const fit = () => setWidth((w) => Math.max(min, Math.min(max, w)));
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [min, max]);

  const commit = useCallback((next: number) => {
    const clamped = Math.round(Math.max(min, Math.min(max, next)));
    live.current = clamped;
    setWidth(clamped);
    try { localStorage.setItem(key, String(clamped)); } catch { /* storage off */ }
  }, [key, min, max]);

  useEffect(() => { onDragChange?.(dragging); }, [dragging, onDragChange]);

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLElement>) => {
    if (e.button !== 0) return;
    const node = panel.current;
    if (!node) return;
    e.preventDefault();
    const grip = e.currentTarget;
    grip.setPointerCapture(e.pointerId);
    const startX = e.clientX;
    const startW = node.getBoundingClientRect().width;
    let pointerX = startX;
    let frame = 0;
    live.current = startW;
    const prevCursor = document.body.style.cursor;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    setDragging(true);

    const apply = () => {
      frame = 0;
      const delta = side === 'left' ? startX - pointerX : pointerX - startX;
      const next = Math.round(Math.max(min, Math.min(max, startW + delta)));
      live.current = next;
      node.style.width = next + 'px';
    };
    const move = (ev: PointerEvent) => {
      pointerX = ev.clientX;
      if (!frame) frame = requestAnimationFrame(apply);
    };
    const up = () => {
      grip.removeEventListener('pointermove', move);
      grip.removeEventListener('pointerup', up);
      grip.removeEventListener('pointercancel', up);
      if (frame) { cancelAnimationFrame(frame); apply(); }
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = '';
      setDragging(false);
      commit(live.current);
    };
    grip.addEventListener('pointermove', move);
    grip.addEventListener('pointerup', up);
    grip.addEventListener('pointercancel', up);
  }, [commit, min, max, side]);

  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    const step = e.shiftKey ? 96 : 24;
    if (e.key === 'ArrowLeft') { e.preventDefault(); commit(width + (side === 'left' ? step : -step)); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); commit(width + (side === 'left' ? -step : step)); }
    else if (e.key === 'Home') { e.preventDefault(); commit(min); }
    else if (e.key === 'End') { e.preventDefault(); commit(max); }
  }, [commit, width, min, max, side]);

  /** Spread onto the grip element. */
  const gripProps = {
    role: 'separator' as const,
    'aria-orientation': 'vertical' as const,
    'aria-valuemin': min,
    'aria-valuemax': max,
    'aria-valuenow': Math.round(width),
    tabIndex: 0,
    onPointerDown,
    onKeyDown,
    onDoubleClick: () => commit(initial)
  };

  return { width, dragging, panelRef: panel, gripProps, setWidth: commit };
}
